/**
 * Hooks for Rich User Profiles
 *
 * useMyRichProfile  – load & save the current user's own profile
 * useUserRichProfiles – load profiles for a set of user IDs (for analysis)
 */

import { useState, useEffect, useCallback } from 'react';
import { getSafeSupabaseClient } from '../lib/supabaseSync';
import { useUserStore } from '../stores/prioritization/userStore';
import {
  type UserRichProfile,
  type UserProfileSegment,
  rowToUserRichProfile,
  userRichProfileToRow,
} from '../types/prioritization/userProfile';

const TABLE = 'user_rich_profiles';

// ── Own profile ───────────────────────────────────────────────────────────────

export interface UseMyRichProfileResult {
  profile: UserRichProfile | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  save: (
    updates: Partial<Omit<UserRichProfile, 'userId' | 'createdAt' | 'updatedAt'>> & { avatarUrl?: string | null }
  ) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useMyRichProfile(): UseMyRichProfileResult {
  const userId = useUserStore((s) => s.userId);

  const [profile, setProfile] = useState<UserRichProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    const client = getSafeSupabaseClient();
    if (!client || !userId || userId === 'unknown') {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await client
      .from(TABLE)
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (fetchError) {
      setError(fetchError.message);
    } else if (data) {
      const p = rowToUserRichProfile(data as Record<string, unknown>);
      setProfile(p);
      useUserStore.getState().setUserAvatarUrl(p.avatarUrl ?? null);
    } else {
      setProfile(null);
      useUserStore.getState().setUserAvatarUrl(null);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const save = useCallback(
    async (updates: Partial<Omit<UserRichProfile, 'userId' | 'createdAt' | 'updatedAt'>> & { avatarUrl?: string | null }): Promise<boolean> => {
      const client = getSafeSupabaseClient();
      if (!client || !userId || userId === 'unknown') return false;

      setSaving(true);
      setError(null);

      const row = userRichProfileToRow({ ...updates, userId });

      const { data, error: upsertError } = await client
        .from(TABLE)
        .upsert(row, { onConflict: 'user_id' })
        .select()
        .single();

      if (upsertError) {
        setError(upsertError.message);
        setSaving(false);
        return false;
      }

      const p = rowToUserRichProfile(data as Record<string, unknown>);
      setProfile(p);
      useUserStore.getState().setUserAvatarUrl(p.avatarUrl ?? null);
      setSaving(false);
      return true;
    },
    [userId]
  );

  return { profile, loading, saving, error, save, refetch: fetchProfile };
}

// ── Profiles for a set of users (analysis / reporting) ───────────────────────

export interface UseUserRichProfilesResult {
  profiles: Map<string, UserRichProfile>;
  segments: Map<string, UserProfileSegment>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches rich profiles for the given user IDs via the
 * get_user_profile_segments RPC (respects visibility).
 * Also supports loading full profiles when `fullProfiles` is true.
 */
export function useUserRichProfiles(
  userIds: string[],
  options: { fullProfiles?: boolean } = {}
): UseUserRichProfilesResult {
  const [profiles, setProfiles] = useState<Map<string, UserRichProfile>>(new Map());
  const [segments, setSegments] = useState<Map<string, UserProfileSegment>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stableKey = userIds.slice().sort().join(',');

  const fetchProfiles = useCallback(async () => {
    const client = getSafeSupabaseClient();
    const ids = userIds.filter(Boolean);
    if (!client || ids.length === 0) {
      setProfiles(new Map());
      setSegments(new Map());
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (options.fullProfiles) {
        const { data, error: fetchError } = await client
          .from(TABLE)
          .select('*')
          .in('user_id', ids);

        if (fetchError) throw fetchError;

        const profileMap = new Map<string, UserRichProfile>();
        const segmentMap = new Map<string, UserProfileSegment>();

        for (const row of data ?? []) {
          const p = rowToUserRichProfile(row as Record<string, unknown>);
          profileMap.set(p.userId, p);
          segmentMap.set(p.userId, toSegment(p));
        }

        setProfiles(profileMap);
        setSegments(segmentMap);
      } else {
        // Lightweight segment RPC
        const { data, error: rpcError } = await client.rpc('get_user_profile_segments', {
          p_user_ids: ids,
        });

        if (rpcError) throw rpcError;

        const segmentMap = new Map<string, UserProfileSegment>();
        for (const row of (data ?? []) as Record<string, unknown>[]) {
          const seg: UserProfileSegment = {
            userId:               row.user_id as string,
            jobFamily:            (row.job_family as UserProfileSegment['jobFamily']) ?? undefined,
            seniorityLevel:       (row.seniority_level as UserProfileSegment['seniorityLevel']) ?? undefined,
            orgLevel:             (row.org_level as string | null) ?? undefined,
            yearsTotalExperience: (row.years_total_experience as number | null) ?? undefined,
            expertiseAreas:       (row.expertise_areas as string[] | null) ?? [],
            productAreas:         (row.product_areas as string[] | null) ?? [],
            department:           (row.department as string | null) ?? undefined,
            teamName:             (row.team_name as string | null) ?? undefined,
          };
          segmentMap.set(seg.userId, seg);
        }

        setSegments(segmentMap);
        setProfiles(new Map());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user profiles');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey, options.fullProfiles]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  return { profiles, segments, loading, error, refetch: fetchProfiles };
}

// ── Internal helper ───────────────────────────────────────────────────────────

function toSegment(p: UserRichProfile): UserProfileSegment {
  return {
    userId:               p.userId,
    jobFamily:            p.jobFamily,
    seniorityLevel:       p.seniorityLevel,
    orgLevel:             p.orgLevel,
    yearsTotalExperience: p.yearsTotalExperience,
    expertiseAreas:       p.expertiseAreas,
    productAreas:         p.productAreas,
    department:           p.department,
    teamName:             p.teamName,
  };
}
